import React from 'react';
import { Box, Typography, Paper, Tooltip, Chip, Skeleton } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';

/* ─── Types ─── */

type SignalStatus = 'red' | 'yellow' | 'none' | null;

interface Signal {
  id: string;
  name: string;
  description: string;
  hover: string;
  category: string;
  isBinary: boolean;
  unit: string;
  yellowLabel?: string;
  redLabel?: string;
  urbanRural?: string;
  years: Record<number, { status: SignalStatus; value?: number; threshold_median?: number; threshold_yellow?: number; threshold_red?: number; detail?: Record<string, number | null> }>;
}

interface CheckEngineDisplayProps {
  signals: Signal[];
  years: number[];
  loading?: boolean;
  categories?: string[];
  sections?: SectionDef[];
}

/* ─── Signal Metadata ─── */

const SIGNAL_METADATA: Record<string, { name: string; description: string; hover: string; unit: string; yellowLabel?: string; redLabel?: string }> = {
  // Section A: Declared RP
  rp_real_estate_per_bed: { name: 'Real Estate', description: 'RP real estate payments per bed vs P70/P90 for same state + urban/rural segment.', hover: 'RP Real Estate Paid \u00F7 Total Beds. Yellow at P70, Red at P90 (state + urban/rural segmented).', unit: '$/bed', yellowLabel: 'State P70', redLabel: 'State P90' },
  rp_mgmt_admin_per_bed: { name: 'Management Fees', description: 'RP admin/management payments per bed vs P70/P90 for same state + urban/rural segment.', hover: 'RP Admin Paid \u00F7 Total Beds. Yellow at P70, Red at P90 (state + urban/rural segmented).', unit: '$/bed', yellowLabel: 'State P70', redLabel: 'State P90' },
  // Section B: Hidden Related Party
  hidden_rp_rent: { name: 'Real Estate', description: 'Abnormally high real estate payments with no disclosed related-party landlord.', hover: 'Rent \u00F7 Resident Days > Yellow at P70, Red at P90 AND no RP declared on A-8-1 for building/rent.', unit: '$/resident-day', yellowLabel: 'State P70', redLabel: 'State P90' },
  hidden_rp_admin: { name: 'Admin & General', description: 'Abnormally high admin/management costs without a disclosed management company.', hover: 'Admin Costs \u00F7 Resident Days > Yellow at P70, Red at P90 AND no RP declared on A-8-1 for admin.', unit: '$/resident-day', yellowLabel: 'State P70', redLabel: 'State P90' },
  hidden_rp_therapy: { name: 'Therapy (PT+OT+SP)', description: 'Abnormally high combined therapy costs without a disclosed therapy company.', hover: '(PT + OT + Speech Costs) \u00F7 Resident Days > Yellow at P70, Red at P90 AND no RP on A-8-1 lines 44\u201346.', unit: '$/resident-day', yellowLabel: 'State P70', redLabel: 'State P90' },
  hidden_rp_drugs: { name: 'Drugs/Pharmacy', description: 'Abnormally high pharmacy costs per resident day without a disclosed pharmacy company.', hover: 'Drug Costs \u00F7 Resident Days > Yellow at P70, Red at P90 AND no RP on A-8-1 line 49.', unit: '$/resident-day', yellowLabel: 'State P70', redLabel: 'State P90' },
  hidden_rp_snf: { name: 'SNF Inpatient', description: 'Abnormally high SNF inpatient routine care costs without a disclosed related party.', hover: 'SNF Routine Costs \u00F7 Resident Days > Yellow at P70, Red at P90 AND no RP on A-8-1 line 30.', unit: '$/resident-day', yellowLabel: 'State P70', redLabel: 'State P90' },
  hidden_rp_benefits: { name: 'Employee Benefits', description: 'Abnormally high benefits costs per resident day without a disclosed benefits provider.', hover: 'Benefits Costs \u00F7 Resident Days > Yellow at P70, Red at P90 AND no RP on A-8-1 line 3.', unit: '$/resident-day', yellowLabel: 'State P70', redLabel: 'State P90' },
  hidden_rp_dietary: { name: 'Dietary', description: 'Abnormally high food service costs per resident day without a disclosed dietary company.', hover: 'Dietary Costs \u00F7 Resident Days > Yellow at P70, Red at P90 AND no RP on A-8-1 line 8.', unit: '$/resident-day', yellowLabel: 'State P70', redLabel: 'State P90' },
  ins_premium: { name: 'Captive Insurance', description: 'Abnormally high premiums without a declared related insurance entity.', hover: 'Malpractice Premiums \u00F7 Total Beds. Yellow at P70, Red at P90.', unit: '$/bed', yellowLabel: 'State P70', redLabel: 'State P90' },
  // Section B: RP Year-over-Year
  rp_rent_yoy: { name: 'RP Real Estate YoY Spike', description: 'Largest single-year swing in rent paid to related parties exceeds state threshold.', hover: 'Max year-over-year % change in RP real estate paid. Yellow at state P70, Red at P90.', unit: '% change', yellowLabel: 'State P70', redLabel: 'State P90' },
  rp_admin_yoy: { name: 'RP Admin/Mgmt YoY Spike', description: 'Largest single-year swing in management fees paid to related parties.', hover: 'Max year-over-year % change in RP admin/mgmt paid. Yellow at state P70, Red at P90.', unit: '% change', yellowLabel: 'State P70', redLabel: 'State P90' },
  // Section C: Financial Control / Extraction Indicators
  due_from_to: { name: 'Intercompany Transfers', description: 'Money flowing between the facility and "other funds" (which ARE related parties).', hover: 'Due From/To Other Funds per bed. Yellow at P70, Red at P90 as a median percentage.', unit: '$/bed', yellowLabel: 'State P70', redLabel: 'State P90' },
  notes_loans_zero: { name: 'Zero Debt', description: 'Facility has zero bank debt \u2014 suggests group-level collateralization and financial control.', hover: 'Notes & Loans Payable = $0. Binary \u2014 Red if $0.', unit: '' },
  loans_from_owners: { name: 'Owner Loans', description: 'Facility borrowed money from its own owners at below-market rates.', hover: 'Loans from Owners > $0. Binary \u2014 Red if any non-zero amount.', unit: '' },
  // (due_from_owners excluded — not in doc)
  // Section D: Staffing Integrity
  rn_lpn_wage: { name: 'RN/LPN Wage Proximity', description: 'RN hourly rate too close to LPN rate, suggesting hour recoding.', hover: 'RN hourly rate \u00F7 LPN hourly rate. Yellow below state P30, Red below P10.', unit: 'ratio', yellowLabel: 'State P30', redLabel: 'State P10' },
  contract_labor: { name: 'Contract Labor', description: 'Abnormally high contract nurse salaries without a declared related party staffing entity.', hover: 'Max contract rate \u00F7 in-house rate across RN/LPN/CNA. Yellow at state P70, Red at P90.', unit: 'ratio', yellowLabel: 'State P70', redLabel: 'State P90' },
  low_rn_rate: { name: 'Low RN Hourly Rate', description: 'Abnormally low RN wage suggests padded RN hours on cost report.', hover: 'RN hourly wage from cost report. Yellow below state P30, Red below P10.', unit: '$/hr', yellowLabel: 'State P30', redLabel: 'State P10' },
  cr_vs_pbj: { name: 'CR vs PBJ Hours Gap', description: 'Cost report nursing hours exceed PBJ-reported hours by >10%.', hover: 'Cost report total nursing hours \u00F7 PBJ total nursing hours > 1.10. Binary flag.', unit: 'ratio' },
  // Section B (Other Flagged): Billing & Revenue
  medicare_los: { name: 'Medicare Length of Stay', description: 'Longer Medicare stays may suggest oversubscribing rehab to maximize billing.', hover: 'Medicare Days \u00F7 Medicare Discharges. Yellow at P70, Red at P90.', unit: 'days', yellowLabel: 'State P70', redLabel: 'State P90' },
  contractual_allowance_pct: { name: 'Contractual Allowances', description: 'Disproportionately large allowances suggest revenue manipulation or billing problems.', hover: '|Contractual Allowances| \u00F7 Total Patient Revenue \u00D7 100. Yellow at P70, Red at P90.', unit: '%', yellowLabel: 'State P70', redLabel: 'State P90' },
  negative_allowances: { name: 'Negative Allowances (UPL)', description: 'Facility received MORE from insurers than billed \u2014 supplemental state payments.', hover: 'Contractual Allowances < $0. Binary: Red if any negative value.', unit: '' },
  rev_income_gap: { name: 'Revenue vs Income Gap', description: 'Abnormally high gap means money is leaving the facility at rates above peers.', hover: '(Net Revenue \u2212 Net Income) \u00F7 Net Revenue \u00D7 100. Yellow at P70, Red at P90.', unit: '%', yellowLabel: 'State P70', redLabel: 'State P90' },
  // Section C (Other Flagged): Balance Sheet YoY
  real_estate_yoy: { name: 'Real Estate YoY Change', description: 'Year-over-year change in total real estate / capital costs (Worksheet A, Line 1).', hover: '(Current Year \u2212 Prior Year) \u00F7 Prior Year \u00D7 100. Yellow at state P70, Red at P90.', unit: '% change', yellowLabel: 'State P70', redLabel: 'State P90' },
  admin_general_yoy: { name: 'Admin & General YoY Change', description: 'Year-over-year change in total admin & general costs (Worksheet A, Line 4).', hover: '(Current Year \u2212 Prior Year) \u00F7 Prior Year \u00D7 100. Yellow at state P70, Red at P90.', unit: '% change', yellowLabel: 'State P70', redLabel: 'State P90' },
};

/* ─── Ordering ─── */

// Explicit signal order within each category — matches the doc exactly
const SIGNAL_ORDER: string[] = [
  // Section A: Declared RP
  'rp_real_estate_per_bed', 'rp_mgmt_admin_per_bed',
  // Section B: Hidden Related Party
  'hidden_rp_rent', 'hidden_rp_admin', 'hidden_rp_therapy', 'hidden_rp_drugs',
  'hidden_rp_snf', 'hidden_rp_benefits', 'hidden_rp_dietary', 'ins_premium',
  'contract_labor',
  // Section B: RP Year-over-Year
  'rp_rent_yoy', 'rp_admin_yoy',
  // Section C: Balance Sheet
  'due_from_to', 'notes_loans_zero', 'loans_from_owners',
  // Section D: Staffing Integrity
  'rn_lpn_wage', 'low_rn_rate',
  // Billing & Revenue
  'medicare_los', 'contractual_allowance_pct', 'negative_allowances', 'rev_income_gap',
  // Balance Sheet YoY
  'real_estate_yoy', 'admin_general_yoy',
];

/* ─── Helpers ─── */

const CATEGORY_ORDER = ['Declared RP', 'Hidden Related Party', 'RP Year-over-Year', 'Balance Sheet', 'Staffing Integrity'];

// Map each signal to its category for placeholder generation
const SIGNAL_CATEGORY: Record<string, string> = {
  rp_real_estate_per_bed: 'Declared RP', rp_mgmt_admin_per_bed: 'Declared RP',
  hidden_rp_rent: 'Hidden Related Party', hidden_rp_admin: 'Hidden Related Party',
  hidden_rp_therapy: 'Hidden Related Party', hidden_rp_drugs: 'Hidden Related Party',
  hidden_rp_snf: 'Hidden Related Party', hidden_rp_benefits: 'Hidden Related Party',
  hidden_rp_dietary: 'Hidden Related Party', ins_premium: 'Hidden Related Party',
  contract_labor: 'Hidden Related Party',
  rp_rent_yoy: 'RP Year-over-Year', rp_admin_yoy: 'RP Year-over-Year',
  due_from_to: 'Balance Sheet', notes_loans_zero: 'Balance Sheet', loans_from_owners: 'Balance Sheet',
  rn_lpn_wage: 'Staffing Integrity', low_rn_rate: 'Staffing Integrity',
  medicare_los: 'Billing & Revenue', contractual_allowance_pct: 'Billing & Revenue',
  negative_allowances: 'Billing & Revenue', rev_income_gap: 'Billing & Revenue',
  real_estate_yoy: 'Balance Sheet YoY', admin_general_yoy: 'Balance Sheet YoY',
};

const DEFAULT_YEARS = [2020, 2021, 2022, 2023, 2024];

const CATEGORY_COLORS: Record<string, string> = {
  'Declared RP': '#00695c',
  'Hidden Related Party': '#1565c0',
  'RP Year-over-Year': '#6a1b9a',
  'Balance Sheet': '#e65100',
  'Staffing Integrity': '#2e7d32',
  'Billing & Revenue': '#1565c0',
  'Balance Sheet YoY': '#e65100',
};

/* ─── Section grouping ─── */

interface SectionDef {
  id: string;
  label: string;
  subtitle: string;
  categories: string[];
  color: string;
}

const SECTIONS: SectionDef[] = [
  { id: 'A', label: 'Section A', subtitle: 'Declared Related Parties', categories: ['Declared RP'], color: '#00695c' },
  { id: 'B', label: 'Section B', subtitle: 'Hidden Related Parties', categories: ['Hidden Related Party'], color: '#1565c0' },
  { id: 'C', label: 'Section C', subtitle: 'Financial Control / Extraction Indicators', categories: ['Balance Sheet'], color: '#e65100' },
  { id: 'D', label: 'Section D', subtitle: 'Staffing Integrity', categories: ['Staffing Integrity'], color: '#2e7d32' },
];

function countByStatus(signals: Signal[], years: number[], status: SignalStatus): number {
  let count = 0;
  for (const s of signals) {
    for (const y of years) {
      if (s.years[y]?.status === status) count++;
    }
  }
  return count;
}

function yearTotals(signals: Signal[], year: number): { red: number; yellow: number } {
  let red = 0, yellow = 0;
  for (const s of signals) {
    if (s.years[year]?.status === 'red') red++;
    if (s.years[year]?.status === 'yellow') yellow++;
  }
  return { red, yellow };
}

/* ─── Dot Component ─── */

const fmtMoney = (v: number) => `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const DETAIL_RENDERERS: Record<string, (detail: Record<string, number | null>) => React.ReactNode> = {
  rn_lpn_wage: (d) => (
    <Box sx={{ mt: 0.5, borderTop: '1px solid rgba(255,255,255,0.15)', pt: 0.5 }}>
      {d.rn_wage != null && <Box>RN Wage: <strong>{fmtMoney(d.rn_wage)}</strong>/hr</Box>}
      {d.lpn_wage != null && <Box>LPN Wage: <strong>{fmtMoney(d.lpn_wage)}</strong>/hr</Box>}
    </Box>
  ),
  low_rn_rate: (d) => (
    <Box sx={{ mt: 0.5, borderTop: '1px solid rgba(255,255,255,0.15)', pt: 0.5 }}>
      {d.rn_wage != null && <Box>RN Wage: <strong>{fmtMoney(d.rn_wage)}</strong>/hr</Box>}
    </Box>
  ),
  cr_vs_pbj: (d) => (
    <Box sx={{ mt: 0.5, borderTop: '1px solid rgba(255,255,255,0.15)', pt: 0.5 }}>
      {d.cr_rn_hours != null && <Box>CR RN Hours: <strong>{Math.round(d.cr_rn_hours).toLocaleString()}</strong></Box>}
      {d.pbj_rn_hours != null && <Box>PBJ RN Hours: <strong>{Math.round(d.pbj_rn_hours).toLocaleString()}</strong></Box>}
      {d.cr_lpn_hours != null && <Box>CR LPN Hours: <strong>{Math.round(d.cr_lpn_hours).toLocaleString()}</strong></Box>}
      {d.pbj_lpn_hours != null && <Box>PBJ LPN Hours: <strong>{Math.round(d.pbj_lpn_hours).toLocaleString()}</strong></Box>}
    </Box>
  ),
  medicare_los: (d) => (
    <Box sx={{ mt: 0.5, borderTop: '1px solid rgba(255,255,255,0.15)', pt: 0.5 }}>
      {d.medicare_days != null && <Box>Medicare Days: <strong>{d.medicare_days.toLocaleString()}</strong></Box>}
      {d.medicare_discharges != null && <Box>Medicare Discharges: <strong>{d.medicare_discharges.toLocaleString()}</strong></Box>}
    </Box>
  ),
  contractual_allowance_pct: (d) => (
    <Box sx={{ mt: 0.5, borderTop: '1px solid rgba(255,255,255,0.15)', pt: 0.5 }}>
      {d.gross_revenue != null && <Box>Gross Revenue: <strong>{fmtMoney(d.gross_revenue)}</strong></Box>}
      {d.net_patient_revenue != null && <Box>Net Patient Revenue: <strong>{fmtMoney(d.net_patient_revenue)}</strong></Box>}
    </Box>
  ),
  negative_allowances: (d) => (
    <Box sx={{ mt: 0.5, borderTop: '1px solid rgba(255,255,255,0.15)', pt: 0.5 }}>
      {d.gross_revenue != null && <Box>Gross Revenue: <strong>{fmtMoney(d.gross_revenue)}</strong></Box>}
      {d.net_patient_revenue != null && <Box>Net Patient Revenue: <strong>{fmtMoney(d.net_patient_revenue)}</strong></Box>}
    </Box>
  ),
  rev_income_gap: (d) => (
    <Box sx={{ mt: 0.5, borderTop: '1px solid rgba(255,255,255,0.15)', pt: 0.5 }}>
      {d.net_patient_revenue != null && <Box>Net Patient Revenue: <strong>{fmtMoney(d.net_patient_revenue)}</strong></Box>}
      {d.total_income != null && <Box>Net Income: <strong>{fmtMoney(d.total_income)}</strong></Box>}
    </Box>
  ),
  real_estate_yoy: (d) => (
    <Box sx={{ mt: 0.5, borderTop: '1px solid rgba(255,255,255,0.15)', pt: 0.5 }}>
      {d.current_year != null && <Box>Current Year: <strong>{fmtMoney(d.current_year)}</strong></Box>}
      {d.prior_year != null && <Box>Prior Year: <strong>{fmtMoney(d.prior_year)}</strong></Box>}
    </Box>
  ),
  admin_general_yoy: (d) => (
    <Box sx={{ mt: 0.5, borderTop: '1px solid rgba(255,255,255,0.15)', pt: 0.5 }}>
      {d.current_year != null && <Box>Current Year: <strong>{fmtMoney(d.current_year)}</strong></Box>}
      {d.prior_year != null && <Box>Prior Year: <strong>{fmtMoney(d.prior_year)}</strong></Box>}
    </Box>
  ),
};

const SignalDot: React.FC<{ status: SignalStatus; isBinary: boolean; name?: string; unit?: string; value?: number; thresholdMedian?: number; thresholdYellow?: number; thresholdRed?: number; yellowLabel?: string; redLabel?: string; signalId?: string; detail?: Record<string, number | null>; urbanRural?: string }> = ({ status, isBinary, name, unit, value, thresholdMedian, thresholdYellow, thresholdRed, yellowLabel, redLabel, signalId, detail, urbanRural }) => {
  if (status === null) {
    return <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.75rem' }}>—</Typography>;
  }
  const u = unit || '';
  const fmtVal = (v: number) => {
    if (u === '$/bed' || u === '$/hr') return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    if (u === '$/resident-day') return `$${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
    if (u === '% change' || u === '%') return `${v.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
    if (u === 'ratio') return v.toLocaleString(undefined, { maximumFractionDigits: 2 }) + 'x';
    if (u === 'days') return v.toLocaleString(undefined, { maximumFractionDigits: 1 }) + ' days';
    return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
  };

  const hasData = value != null || thresholdYellow != null || thresholdRed != null;

  const statusLabel = status === 'none'
    ? 'Below threshold'
    : isBinary
      ? 'Condition present'
      : status === 'red' ? 'Alert' : 'Caution';
  const statusColor = status === 'red' ? '#ff8a80' : status === 'yellow' ? '#fff59d' : '#a5d6a7';

  const tooltipContent = (
    <Box sx={{ fontSize: '0.7rem', lineHeight: 1.6 }}>
      {name && <Box fontWeight={700} sx={{ mb: 0.25 }}>{name}</Box>}
      <Box sx={{ color: statusColor }}>{statusLabel}</Box>
      {value != null && <Box>Facility: <strong>{fmtVal(value)}</strong>{u && !u.startsWith('$') && !u.startsWith('%') ? ` (${u})` : ''}</Box>}
      {urbanRural && <Box>Segment: {urbanRural === 'U' ? 'Urban' : urbanRural === 'R' ? 'Rural' : urbanRural}</Box>}
      {thresholdMedian != null && <Box>State Median: {fmtVal(thresholdMedian)}</Box>}
      {thresholdYellow != null && <Box>{yellowLabel || 'Yellow'}: {fmtVal(thresholdYellow)}</Box>}
      {thresholdRed != null && <Box>{redLabel || 'Red'}: {fmtVal(thresholdRed)}</Box>}
      {detail && signalId && DETAIL_RENDERERS[signalId]?.(detail)}
    </Box>
  );

  if (status === 'none') {
    if (!hasData) {
      return <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: '#e0e0e0', mx: 'auto' }} />;
    }
    return (
      <Tooltip title={tooltipContent} arrow>
        <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: '#e0e0e0', mx: 'auto', cursor: 'pointer' }} />
      </Tooltip>
    );
  }

  const color = status === 'red' ? '#d32f2f' : '#fbc02d';
  const shadow = status === 'red' ? '0 1px 4px rgba(211,47,47,0.4)' : '0 1px 4px rgba(251,192,45,0.4)';

  return (
    <Tooltip title={tooltipContent} arrow>
      <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: color, mx: 'auto', boxShadow: shadow, cursor: 'pointer' }} />
    </Tooltip>
  );
};

/* ─── Loading Skeleton ─── */

const LoadingSkeleton: React.FC = () => (
  <Box>
    <Box display="flex" gap={2} mb={3} flexWrap="wrap">
      {[1, 2, 3, 4].map(i => (
        <Paper key={i} variant="outlined" sx={{ px: 2.5, py: 1.5, borderRadius: 2, flex: '1 1 160px' }}>
          <Skeleton width={80} height={16} />
          <Skeleton width={60} height={40} />
          <Skeleton width={120} height={14} />
        </Paper>
      ))}
    </Box>
    <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <Skeleton key={i} height={36} sx={{ mb: 0.5 }} />
      ))}
    </Paper>
  </Box>
);

/* ─── Main Component ─── */

const CheckEngineDisplay: React.FC<CheckEngineDisplayProps> = ({ signals, years, loading, categories, sections: sectionsProp }) => {
  if (loading) return <LoadingSkeleton />;

  if (!signals) signals = [];

  // Ensure years always has values
  const effectiveYears = years && years.length > 0 ? years : DEFAULT_YEARS;

  // Filter by categories if provided
  const filteredSignals = categories
    ? signals.filter(s => categories.includes(s.category))
    : signals;

  // Build a set of existing signal IDs
  const existingIds = new Set(filteredSignals.map(s => s.id));

  // Generate placeholder signals for any missing ones in the requested categories
  const activeCats = categories || CATEGORY_ORDER;
  const allSignals = [...filteredSignals];
  for (const sigId of SIGNAL_ORDER) {
    if (existingIds.has(sigId)) continue;
    const cat = SIGNAL_CATEGORY[sigId];
    if (!cat || !activeCats.includes(cat)) continue;
    const meta = SIGNAL_METADATA[sigId];
    if (!meta) continue;
    allSignals.push({
      id: sigId,
      name: meta.name,
      description: meta.description,
      hover: meta.hover,
      category: cat,
      isBinary: false,
      unit: meta.unit,
      yellowLabel: meta.yellowLabel,
      redLabel: meta.redLabel,
      years: {},
    } as Signal);
  }

  const totalRed = countByStatus(allSignals, effectiveYears, 'red');
  const totalYellow = countByStatus(allSignals, effectiveYears, 'yellow');
  const compositeScore = totalRed * 2 + totalYellow;
  const maxPossible = allSignals.length * effectiveYears.length * 2;
  const normalizedPct = maxPossible > 0 ? Math.round((compositeScore / maxPossible) * 100) : 0;

  // Build sections: each section has one or more category groups
  const activeSections = (sectionsProp || SECTIONS)
    .map(sec => {
      const catGroups = sec.categories
        .filter(cat => !categories || categories.includes(cat))
        .map(cat => ({
          category: cat,
          signals: allSignals
            .filter(s => s.category === cat && SIGNAL_ORDER.includes(s.id))
            .sort((a, b) => SIGNAL_ORDER.indexOf(a.id) - SIGNAL_ORDER.indexOf(b.id)),
        }))
        .filter(g => g.signals.length > 0);
      return { ...sec, catGroups };
    })
    .filter(sec => sec.catGroups.length > 0);

  return (
    <Box>
      {/* Summary cards hidden on both tabs — Composite Score / Red Flags / Yellow Flags / Scoring
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, borderRadius: 2, flex: '1 1 160px' }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
            Composite Score
          </Typography>
          <Box display="flex" alignItems="baseline" gap={0.5}>
            <Typography variant="h4" fontWeight={700} sx={{ color: 'text.primary' }}>
              {compositeScore}
            </Typography>
            <Typography variant="body2" color="text.secondary">pts</Typography>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, borderRadius: 2, flex: '1 1 120px' }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
            Red Flags
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <ErrorIcon sx={{ color: '#d32f2f', fontSize: '1.2rem' }} />
            <Typography variant="h4" fontWeight={700} color="error">{totalRed}</Typography>
          </Box>
          <Typography variant="caption" color="text.disabled">
            A red flag indicates a data point above the 90th state percentile
          </Typography>
        </Paper>

        <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, borderRadius: 2, flex: '1 1 120px' }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
            Yellow Flags
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningAmberIcon sx={{ color: '#fbc02d', fontSize: '1.2rem' }} />
            <Typography variant="h4" fontWeight={700} sx={{ color: '#f9a825' }}>{totalYellow}</Typography>
          </Box>
          <Typography variant="caption" color="text.disabled">
            A yellow flag indicates a data point above the 70th state percentile
          </Typography>
        </Paper>

        <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, borderRadius: 2, flex: '1 1 120px' }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
            Scoring
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Red = 2 pts, Yellow = 1 pt
          </Typography>
          <Typography variant="caption" color="text.disabled">
            {totalRed} x2 + {totalYellow} x1 = {compositeScore}
          </Typography>
        </Paper>
      </Box>
      */}

      {/* Signal Grid */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {/* Header row */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `200px 1fr 1fr repeat(${effectiveYears.length}, 70px)`,
            bgcolor: '#f5f5f5',
            borderBottom: '2px solid',
            borderColor: 'divider',
            px: 2,
            py: 1,
            alignItems: 'center',
          }}
        >
          <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
            Signal
          </Typography>
          <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
            Description
          </Typography>
          <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
            Formula
          </Typography>
          {effectiveYears.map(y => (
            <Typography key={y} variant="caption" fontWeight={700} align="center" sx={{ fontSize: '0.7rem' }}>
              {y}
            </Typography>
          ))}
        </Box>

        {/* Sections (A, B, C, D) */}
        {activeSections.map((section) => (
          <Box key={section.id}>
            {/* Section header */}
            <Box sx={{
              px: 2, py: 1,
              bgcolor: `${section.color}12`,
              borderBottom: '2px solid',
              borderTop: '2px solid',
              borderColor: `${section.color}40`,
            }}>
              <Typography variant="subtitle2" fontWeight={800} sx={{ color: section.color, letterSpacing: 0.5 }}>
                {section.label}: {section.subtitle}
              </Typography>
            </Box>

            {/* Category groups within this section */}
            {section.catGroups.map(({ category, signals: catSignals }) => (
              <Box key={category}>
                {/* Category sub-header (only if section has multiple categories) */}
                {section.catGroups.length > 1 && (
                  <Box sx={{ px: 2, py: 0.5, bgcolor: `${CATEGORY_COLORS[category] || '#757575'}08`, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" fontWeight={700} sx={{ color: CATEGORY_COLORS[category] || '#757575', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: 0.5 }}>
                      {category}
                    </Typography>
                    <Chip
                      label={`${catSignals.length}`}
                      size="small"
                      sx={{ ml: 1, height: 16, fontSize: '0.55rem', fontWeight: 600 }}
                    />
                  </Box>
                )}

                {/* Signal rows */}
                {catSignals.map((signal, idx) => (
                  <Box
                    key={signal.id}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: `200px 1fr 1fr repeat(${effectiveYears.length}, 70px)`,
                      px: 2,
                      py: 0.75,
                      alignItems: 'center',
                      borderBottom: idx < catSignals.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Tooltip title={signal.hover || ''} arrow placement="right">
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, cursor: signal.hover ? 'help' : 'default' }}>
                        {signal.name}
                      </Typography>
                    </Tooltip>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1.3, pr: 1 }}>
                      {signal.description}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', lineHeight: 1.3, pr: 1 }}>
                      {signal.hover}
                    </Typography>
                    {effectiveYears.map(y => {
                      const yearData = signal.years[y];
                      return (
                        <Box key={y} sx={{ textAlign: 'center' }}>
                          <SignalDot
                            status={yearData?.status ?? null}
                            isBinary={signal.isBinary}
                            name={signal.name}
                            unit={signal.unit}
                            value={yearData?.value}
                            thresholdMedian={yearData?.threshold_median}
                            thresholdYellow={yearData?.threshold_yellow}
                            thresholdRed={yearData?.threshold_red}
                            yellowLabel={signal.yellowLabel}
                            redLabel={signal.redLabel}
                            signalId={signal.id}
                            detail={yearData?.detail}
                            urbanRural={signal.urbanRural}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        ))}

        {/* Footer: per-year totals */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `200px 1fr 1fr repeat(${effectiveYears.length}, 70px)`,
            px: 2,
            py: 1,
            bgcolor: '#f5f5f5',
            borderTop: '2px solid',
            borderColor: 'divider',
            alignItems: 'center',
          }}
        >
          <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', fontSize: '0.65rem' }}>
            Totals
          </Typography>
          <Box />
          <Box />
          {effectiveYears.map(y => {
            const t = yearTotals(allSignals, y);
            return (
              <Box key={y} sx={{ textAlign: 'center' }}>
                {t.red > 0 && (
                  <Typography variant="caption" fontWeight={700} sx={{ color: '#d32f2f', display: 'block', fontSize: '0.7rem' }}>
                    {t.red}R
                  </Typography>
                )}
                {t.yellow > 0 && (
                  <Typography variant="caption" fontWeight={600} sx={{ color: '#f9a825', display: 'block', fontSize: '0.7rem' }}>
                    {t.yellow}Y
                  </Typography>
                )}
                {t.red === 0 && t.yellow === 0 && (
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>—</Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
};

export { SIGNAL_METADATA, SIGNAL_ORDER, SIGNAL_CATEGORY };
export type { Signal, CheckEngineDisplayProps, SectionDef };
export default CheckEngineDisplay;
